import { Link, createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { ArrowLeft, Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSettlement, useSettlementRows } from '@/api/queries';
import { Empty, ErrorBox } from '@/components/empty';
import { Json } from '@/components/json-view';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/_authed/settlements/$fileId')({
  component: SettlementDetail,
});

const STATUS_VARIANT: Record<string, React.ComponentProps<typeof Badge>['variant']> = {
  matched: 'success',
  mismatch: 'warning',
  missing_in_hub: 'destructive',
  missing_in_psp: 'destructive',
};

function SettlementDetail() {
  const { t } = useTranslation();
  const { fileId } = Route.useParams();
  const file = useSettlement(fileId);
  const [statusFilter, setStatusFilter] = useState<string>('__all');
  const rows = useSettlementRows(fileId, {
    status_filter: statusFilter === '__all' ? undefined : statusFilter,
    limit: 500,
  });

  if (file.isError) return <ErrorBox message={t('settlements.detail.not_found')} />;
  if (file.isLoading || !file.data) return <Empty title={t('common.loading')} />;

  const f = file.data;
  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/settlements">
            <ArrowLeft className="h-4 w-4" /> {t('common.back')}
          </Link>
        </Button>
      </div>

      <header className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{f.filename}</h1>
          <p className="text-sm text-muted-foreground font-mono">
            {f.psp_code} · uploaded {dayjs(f.created_at).format('YYYY-MM-DD HH:mm')}
          </p>
        </div>
      </header>

      <section className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-5">
        <Stat label={t('settlements.cols.rows')} value={f.row_count} />
        <Stat label={t('settlements.cols.matched')} value={f.matched_count} variant="success" />
        <Stat label={t('settlements.cols.mismatch')} value={f.mismatch_count} variant="warning" />
        <Stat
          label={t('settlements.cols.missing_in_hub')}
          value={f.missing_in_hub_count}
          variant="destructive"
        />
        <Stat
          label={t('settlements.cols.missing_in_psp')}
          value={f.missing_in_psp_count}
          variant="destructive"
        />
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>{t('settlements.detail.rows')}</span>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <a
                  href={`/admin/settlements/${fileId}/export.csv${
                    statusFilter !== '__all' ? `?status_filter=${statusFilter}` : ''
                  }`}
                  download
                >
                  <Download className="h-4 w-4" /> {t('common.export_csv')}
                </a>
              </Button>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">{t('common.all')}</SelectItem>
                  <SelectItem value="matched">matched</SelectItem>
                  <SelectItem value="mismatch">mismatch</SelectItem>
                  <SelectItem value="missing_in_hub">missing_in_hub</SelectItem>
                  <SelectItem value="missing_in_psp">missing_in_psp</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('settlements.detail.status')}</TableHead>
                <TableHead>{t('settlements.detail.order_ref')}</TableHead>
                <TableHead>{t('settlements.detail.psp_ref')}</TableHead>
                <TableHead>{t('settlements.detail.amount')}</TableHead>
                <TableHead>{t('settlements.detail.psp_status')}</TableHead>
                <TableHead>{t('settlements.detail.diff')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.data?.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[r.status] ?? 'outline'}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.merchant_order_ref ?? '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {r.psp_ref ?? '—'}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {r.amount_minor != null ? r.amount_minor : '—'}
                  </TableCell>
                  <TableCell className="text-xs">{r.psp_status ?? '—'}</TableCell>
                  <TableCell>
                    {r.diff && Object.keys(r.diff).length > 0 ? <Json data={r.diff} /> : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!rows.isLoading && rows.data?.length === 0 && (
            <Empty title={t('settlements.detail.no_rows')} className="border-0 mt-3" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant?: React.ComponentProps<typeof Badge>['variant'];
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">
          {variant && value > 0 ? <Badge variant={variant}>{value}</Badge> : value}
        </p>
      </CardContent>
    </Card>
  );
}
